import type { LatLngTuple, PathOptions } from "leaflet";

import { MapFeatureDTO } from "../../DTOs/Production/MapFeatureDTO";

export type FeatureGeometry =
  | { kind: "point"; position: LatLngTuple }
  | { kind: "line"; path: LatLngTuple[] }
  | { kind: "polygon"; rings: LatLngTuple[][] };

export type MapFeature = {
  id: number;
  featureClass: string;
  name: string | null;
  minZoom: number;
  sortOrder: number;
  geometry: FeatureGeometry;
};

export type FeatureStyleName =
  | "site-extent"
  | "water"
  | "tsf-crest"
  | "dump-toe"
  | "dump-lift"
  | "bench-crest"
  | "ramp-formation"
  | "plant"
  | "building"
  | "laydown"
  | "an-pad"
  | "conveyor"
  | "magazine"
  | "exclusion-zone"
  | "tower";

export const SITE_EXTENT_FEATURE_CLASS = "site-extent";

export const FEATURE_STYLE_BY_CLASS: Record<string, FeatureStyleName> = {
  "site-extent": "site-extent",
  "water-dam": "water",
  "sediment-pond": "water",
  "tsf-pond": "water",
  "tsf-crest": "tsf-crest",
  "process-plant": "plant",
  "stockpile-cos": "plant",
  "conveyor": "conveyor",
  "building": "building",
  "laydown": "laydown",
  "an-pad": "an-pad",
  "magazine": "magazine",
  "exclusion-zone": "exclusion-zone",
  "comms-tower": "tower",
  "ramp-formation": "ramp-formation",
  "dump-toe": "dump-toe",
  "dump-lift": "dump-lift",
  "bench-crest": "bench-crest",
};

export type FeatureStyle = {
  stroke?: { colour: string; weight: number; dash?: string };
  fill?: string;
};

export const FEATURE_STYLES: Record<FeatureStyleName, FeatureStyle> = {
  "site-extent": { fill: "#F3F0EA" },
  "water": { fill: "#7DD3FC", stroke: { colour: "#0284C7", weight: 1 } },
  "tsf-crest": { stroke: { colour: "#57534E", weight: 2.5 } },
  "dump-toe": { stroke: { colour: "#3F6212", weight: 2 } },
  "dump-lift": { stroke: { colour: "#4D7C0F", weight: 1 } },
  "bench-crest": { stroke: { colour: "#64748B", weight: 1 } },
  "ramp-formation": { fill: "#E7E5E4" },
  "plant": { fill: "#A8A29E", stroke: { colour: "#57534E", weight: 1 } },
  "building": { fill: "#A8A29E", stroke: { colour: "#57534E", weight: 1 } },
  "laydown": { stroke: { colour: "#78716C", weight: 1, dash: "4 4" } },
  "an-pad": { stroke: { colour: "#78716C", weight: 1, dash: "4 4" } },
  "conveyor": { stroke: { colour: "#292524", weight: 3 } },
  "magazine": { fill: "#FCA5A5", stroke: { colour: "#DC2626", weight: 1 } },
  "exclusion-zone": { stroke: { colour: "#DC2626", weight: 1, dash: "8 6" } },
  "tower": { fill: "#FAFAF9", stroke: { colour: "#44403C", weight: 1.5 } },
};

export const UNKNOWN_FEATURE_STYLE: FeatureStyle = { stroke: { colour: "#78716C", weight: 1 } };

export const FEATURE_POINT_RADIUS = 3;

export function featureStyle(featureClass: string): FeatureStyle {
  if (Object.hasOwn(FEATURE_STYLE_BY_CLASS, featureClass))
    return FEATURE_STYLES[FEATURE_STYLE_BY_CLASS[featureClass]];

  return UNKNOWN_FEATURE_STYLE;
}

// Note: Leaflet copies every key it is given over a path's options, undefined included, and never drops one.
// So weight is left out when there is no stroke, since a polygon pads its clip bounds by its weight and an
// undefined one makes them NaN; dashArray is always set, so a feature whose class changes loses its old dash.
export function featurePathOptions(featureClass: string): PathOptions {
  const { stroke, fill } = featureStyle(featureClass);

  return {
    stroke: stroke != null,
    ...(stroke ? { color: stroke.colour, weight: stroke.weight } : {}),
    dashArray: stroke?.dash,
    opacity: 1,
    fill: fill != null,
    ...(fill ? { fillColor: fill } : {}),
    fillOpacity: 1,
  };
}

export function isFeatureShown(feature: { featureClass: string; minZoom: number }, zoom: number, sitePlan: boolean): boolean {
  if (feature.featureClass === SITE_EXTENT_FEATURE_CLASS && !sitePlan) return false;
  return zoom >= feature.minZoom;
}

function toLatLng(coordinate: unknown): LatLngTuple | null {
  if (!Array.isArray(coordinate) || coordinate.length < 2) return null;

  const [lon, lat] = coordinate;
  return Number.isFinite(lon) && Number.isFinite(lat) ? [lat, lon] : null;
}

function toPath(coordinates: unknown): LatLngTuple[] | null {
  if (!Array.isArray(coordinates)) return null;

  const path = coordinates.map(toLatLng);
  return path.every(p => p != null) ? path as LatLngTuple[] : null;
}

function openRing(ring: LatLngTuple[]): LatLngTuple[] {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return ring.length > 1 && first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
}

export function parseFeatureGeometry(raw: string): FeatureGeometry | null {
  try {
    const parsed = JSON.parse(raw);
    const geometry = parsed?.geometry ?? parsed;

    if (geometry?.type === "Point") {
      const position = toLatLng(geometry.coordinates);
      return position ? { kind: "point", position } : null;
    }

    if (geometry?.type === "LineString") {
      const path = toPath(geometry.coordinates);
      return path && path.length >= 2 ? { kind: "line", path } : null;
    }

    if (geometry?.type === "Polygon" && Array.isArray(geometry.coordinates)) {
      const rings = geometry.coordinates.map(toPath);
      if (rings.length === 0 || rings.some((r: LatLngTuple[] | null) => r == null)) return null;

      const open = (rings as LatLngTuple[][]).map(openRing);
      return open.every(r => r.length >= 3) ? { kind: "polygon", rings: open } : null;
    }

    return null;
  } catch {
    return null;
  }
}

export function toMapFeatures(rows: MapFeatureDTO[]): MapFeature[] {
  return rows
    .flatMap((row): MapFeature[] => {
      const geometry = parseFeatureGeometry(row.geometry ?? "");
      if (!geometry) return [];

      return [{
        id: row.mapFeatureId,
        featureClass: row.featureClass,
        name: row.name ?? null,
        minZoom: row.minZoom ?? 0,
        sortOrder: row.sortOrder ?? 0,
        geometry,
      }];
    })
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}
